import { FieldStructureMap } from '../../../service-request-form-response.mapper.util';
import { ChecklistInsertConfig } from '../../shared/service-request-form-checklist-section/service-request-form-checklist-section.component';
import {
  buildChecklistField,
  buildTechnicianSignatureFieldStructure,
} from '../shared/static-form-fields.util';
import { TechnicianSignatureLayoutEntry } from '../shared/static-form-section.util';

const field = (
  controlKey: string,
  label: string,
  sequence: number,
  overrides: Partial<FieldStructureMap[string]> = {},
): [string, FieldStructureMap[string]] => [
  controlKey,
  {
    type: overrides.type ?? 'TEXT',
    label,
    sequence,
    required: overrides.required ?? false,
    ...overrides,
  },
];

export const HI_GENERAL_INFO_LEFT_KEYS = [
  'serviceRequestNo',
  'serviceRequestType',
  'dateCreated',
  'createdBy',
  'siteContactName',
] as const;

export const HI_GENERAL_INFO_RIGHT_KEYS = [
  'bulkPlant',
  'plantAddress',
  'plantCoordinates',
  'ownership',
  'vesselSerialNo',
] as const;

export const HI_HOSE_GRID_LEFT_FIELD_ENTRIES: [string, { type?: 'textarea'; required?: boolean; maxLength?: number }][] = [
  ['hoseLocationWithinPlant', { type: 'textarea', required: true, maxLength: 100 }],
  ['hoseUnitNo', { type: 'textarea', required: true, maxLength: 100 }],
  ['hoseAssembler', { type: 'textarea', required: true, maxLength: 100 }],
  ['hoseHoseId', { type: 'textarea', required: true, maxLength: 100 }],
  ['hoseCertification', { type: 'textarea', required: true, maxLength: 100 }],
];

export const HI_HOSE_GRID_RIGHT_FIELD_ENTRIES: [string, { type?: 'text' | 'textarea'; required?: boolean }][] = [
  ['hoseAssemblyWorkingPressure', { required: true }],
  ['hoseTestPressure', { required: true }],
  ['hoseTestDurationMin', { required: true }],
  ['hoseMediumUsed', { required: true }],
];

export const HI_HOSE_INSTANCE_FIELD_KEYS = [
  'hoseLocationWithinPlant',
  'hoseAssemblyWorkingPressure',
  'hoseUnitNo',
  'hoseTestPressure',
  'hoseAssembler',
  'hoseTestDurationMin',
  'hoseHoseId',
  'hoseMediumUsed',
  'hoseCertification',
  'hoseVisualConditionOuterCovering',
  'hoseVisualKinksOrDeformations',
  'hoseVisualEndSlippage',
  'hoseVisualMarkings',
  'hoseVisualBulgesOrSoftSpots',
  'hoseVisualHoseFoundSatisfactory',
  'hoseAdditionalComments',
  'hoseInspectionPhotos',
] as const;

export const HI_HOSE_VISUAL_CHECKLIST_KEYS = [
  'hoseVisualConditionOuterCovering',
  'hoseVisualKinksOrDeformations',
  'hoseVisualEndSlippage',
  'hoseVisualMarkings',
  'hoseVisualBulgesOrSoftSpots',
  'hoseVisualHoseFoundSatisfactory',
] as const;

export const HI_HOSE_INSPECTION_PHOTO_MAX_COUNT = 3;

export const HI_HOSE_VISUAL_Q6_CONTROL_KEY = 'hoseVisualHoseFoundSatisfactory';

export const HI_HOSE_VISUAL_INSERT_AFTER_ITEMS: ChecklistInsertConfig[] = [
  {
    afterItemNumber: 6,
    photoControlKey: 'hoseInspectionPhotos',
    maxPhotoCount: HI_HOSE_INSPECTION_PHOTO_MAX_COUNT,
  },
];

export const HI_INSPECTION_LEFT_FIELD_ENTRIES: [string, { readOnly?: boolean }][] = [
  ['inspectionNo', { readOnly: true }],
  ['inspectionType', { readOnly: true }],
  ['inspectionDate', {}],
];

export const HI_INSPECTION_OUTCOME_OPTIONS = [
  { label: 'Pass', value: 'Pass' },
  { label: 'Fail', value: 'Fail' },
] as const;

export const HI_INSPECTION_RIGHT_FIELD_ENTRIES: [string, { type?: 'text' | 'select' | 'textarea'; required?: boolean; maxLength?: number }][] = [
  ['inspectionOutcome', { type: 'select', required: true }],
  ['outcomeRemarks', { type: 'textarea', required: true, maxLength: 100 }],
];

export const HI_SIGNOFF_LAYOUT: TechnicianSignatureLayoutEntry[] = [
  { controlKey: 'technicianName', type: 'userLookup', column: 'left', required: true },
  { controlKey: 'inspectorDesignation', type: 'textarea', column: 'left', required: true, maxLength: 100 },
  { controlKey: 'inspectorRotNumber', type: 'textarea', column: 'left', required: true, maxLength: 100 },
  { controlKey: 'rotExpiryDate', type: 'date', column: 'left', required: true },
  { controlKey: 'serviceDate', type: 'text', column: 'right', readOnly: true },
  { controlKey: 'locationLongitude', type: 'text', column: 'right', readOnly: true },
  { controlKey: 'locationLatitude', type: 'text', column: 'right', readOnly: true },
];

export const HI_SIGNOFF_FOOTER_FIELD_ENTRIES = [
  ['signOffComments'],
  ['actionTaken'],
] as const;

const buildHiGeneralInfoFields = (): [string, FieldStructureMap[string]][] => [
  field('serviceRequestNo', 'Service Request No.', 2, { readOnly: true }),
  field('serviceRequestType', 'Service Request Type', 3, { readOnly: true }),
  field('dateCreated', 'Date Created', 4, { readOnly: true }),
  field('createdBy', 'Created By', 5, { readOnly: true }),
  field('siteContactName', 'Site Contact Name', 6, { readOnly: true }),
  field('bulkPlant', 'Bulk Plant', 7, { readOnly: true }),
  field('plantAddress', 'Address', 8, { readOnly: true }),
  field('plantCoordinates', 'Longitude, Latitude', 9, { readOnly: true }),
  field('ownership', 'Ownership', 10, { readOnly: true }),
  field('vesselSerialNo', 'Vessel Serial No.', 11, { readOnly: true }),
];

const hoseInstanceFields = (): [string, FieldStructureMap[string]][] => [
  field('hoseLocationWithinPlant', 'Location of Hose Within Plant', 30, {
    type: 'TEXT_AREA',
    required: true,
    maxLength: 100,
  }),
  field('hoseAssemblyWorkingPressure', 'Hose Assembly Working Pressure', 31, { required: true }),
  field('hoseUnitNo', 'Unit No', 32, { type: 'TEXT_AREA', required: true, maxLength: 100 }),
  field('hoseTestPressure', 'Test Pressure', 33, { required: true }),
  field('hoseAssembler', 'Hose Assembler', 34, { type: 'TEXT_AREA', required: true, maxLength: 100 }),
  field('hoseTestDurationMin', 'Test Duration (min)', 35, { required: true }),
  field('hoseHoseId', 'Hose ID', 36, { type: 'TEXT_AREA', required: true, maxLength: 100 }),
  field('hoseMediumUsed', 'Medium Used', 37, { required: true }),
  field('hoseCertification', 'Certification', 38, { type: 'TEXT_AREA', required: true, maxLength: 100 }),
  ...HI_HOSE_VISUAL_CHECKLIST_KEYS.map((controlKey, index) =>
    buildChecklistField(controlKey, hoseVisualChecklistLabel(controlKey), 39 + index),
  ),
  field('hoseAdditionalComments', 'Additional Comments', 50, { type: 'TEXT_AREA', maxLength: 150 }),
  field('hoseInspectionPhotos', 'Photos', 51, {
    type: 'IMAGE',
    maxPhotoCount: HI_HOSE_INSPECTION_PHOTO_MAX_COUNT,
  }),
];

function hoseVisualChecklistLabel(controlKey: string): string {
  const labels: Record<string, string> = {
    hoseVisualConditionOuterCovering: 'Condition of outer Covering',
    hoseVisualKinksOrDeformations: 'Kinks or Deformations',
    hoseVisualEndSlippage: 'End Slippage',
    hoseVisualMarkings: 'Markings',
    hoseVisualBulgesOrSoftSpots: 'Bulges or Soft Spots',
    hoseVisualHoseFoundSatisfactory: 'Hose found to be satisfactory',
  };
  return labels[controlKey] ?? controlKey;
}

export const HI_FIELD_STRUCTURE: FieldStructureMap = Object.fromEntries([
  ...buildHiGeneralInfoFields(),
  ...hoseInstanceFields(),
  field('signOffComments', 'Comments', 60, { type: 'TEXT_AREA', maxLength: 150 }),
  field('actionTaken', 'Action Taken', 61, { type: 'TEXT_AREA', maxLength: 150 }),
  field('inspectionNo', 'Inspection No.', 70, { readOnly: true }),
  field('inspectionType', 'Inspection Type', 71, { readOnly: true }),
  field('inspectionDate', 'Inspection Date', 72, { type: 'DATE' }),
  field('inspectionOutcome', 'Inspection Outcome', 73, {
    type: 'SINGLE',
    required: true,
    subQuestionLabels: ['Pass', 'Fail'],
  }),
  field('outcomeRemarks', 'Outcome Remarks', 74, {
    type: 'TEXT_AREA',
    required: true,
    maxLength: 100,
  }),
  field('technicianComments', 'Technician Comments/Remarks', 75, {
    type: 'TEXT_AREA',
    maxLength: 150,
  }),
  ...Object.entries(buildTechnicianSignatureFieldStructure(80)),
]);
